import cx from 'classnames'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Divider } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import useDrawersAutohiding from './useDrawersAutohiding'
import FoldableDrawer, { useContentStyles } from './FoldableDrawer'
import { useWindowWidth } from './useWindowWidthEffect'
import { SidebarControls, SidebarControlsContext } from './sidebarControls'

type Props = {
  sidebar?: JSX.Element
  collapsedSidebarControl?: JSX.Element
  children: React.ReactNode
  infoarea?: JSX.Element
  sidebarWidth?: number
  minExpandedSidebarWidth?: number
  infoareaWidth?: number
  forceShowSidebar?: boolean
  sidebarPaperBackgroundColor?: string
  infoareaPaperBackgroundColor?: string
  selectedUid?: string
}

const MIN_CONTENT_WIDTH = 370
const MAX_EXPANDED_SIDEBAR_WIDTH = 1300

const useStyles = makeStyles((theme) => ({
  collapsedSidebarControl: {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    top: theme.spacing(2),
    left: theme.spacing(2),
    zIndex: theme.zIndex.drawer,
  },
  collapsedSidebarDivider: {
    height: theme.spacing(3),
  },
}))

const Container = ({
  children,
  infoarea,
  sidebar,
  collapsedSidebarControl,
  sidebarWidth = 370,
  minExpandedSidebarWidth = 0,
  infoareaWidth = 370,
  forceShowSidebar,
  sidebarPaperBackgroundColor,
  infoareaPaperBackgroundColor,
  selectedUid,
}: Props) => {
  const styles = useStyles()
  const hasInfoArea = Boolean(infoarea)
  const hasSidebar = Boolean(sidebar)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [sidebarExpanded, setSidebarExpanded] = useState(false)
  const [infoareaOpen, setInfoAreaOpen] = useState(hasInfoArea)

  const windowWidth = useWindowWidth() ?? 0

  const isCollapsed = sidebarCollapsed && !forceShowSidebar

  const infoareaSpace = hasInfoArea && infoareaOpen ? infoareaWidth : 0
  const widthLeavingContentVisible = Math.min(
    MAX_EXPANDED_SIDEBAR_WIDTH,
    windowWidth - MIN_CONTENT_WIDTH - infoareaSpace
  )
  const expandedSidebarWidth = Math.max(sidebarWidth, minExpandedSidebarWidth, widthLeavingContentVisible)
  const canExpandSidebar = windowWidth >= expandedSidebarWidth + infoareaSpace

  const isExpanded = sidebarExpanded && !isCollapsed && canExpandSidebar
  const sidebarPaperWidth = isExpanded ? expandedSidebarWidth : sidebarWidth

  const sidebarStyleProps = useMemo(() => ({ alignment: 'left' as const, width: sidebarWidth }), [sidebarWidth])
  const infoareaStyleProps = useMemo(() => ({ alignment: 'right' as const, width: infoareaWidth }), [infoareaWidth])

  const sidebarContentStyles = useContentStyles(sidebarStyleProps)
  const infoareaContentStyles = useContentStyles(infoareaStyleProps)

  const toggleInfoarea = () => setInfoAreaOpen(!infoareaOpen)

  const toggleCollapsed = useCallback(() => setSidebarCollapsed((collapsed) => !collapsed), [])
  const toggleExpanded = useCallback(
    () => setSidebarExpanded((expanded) => (expanded ? false : canExpandSidebar)),
    [canExpandSidebar]
  )
  const shrink = useCallback(() => setSidebarExpanded(false), [])
  const setSidebarOpen = useCallback((open: boolean) => setSidebarCollapsed(!open), [])

  const sidebarControls: SidebarControls = useMemo(
    () => ({ isCollapsed, isExpanded, toggleCollapsed, toggleExpanded, shrink }),
    [isCollapsed, isExpanded, toggleCollapsed, toggleExpanded, shrink]
  )

  useDrawersAutohiding({
    setSidebarOpen,
    setInfoAreaOpen,
    sidebarWidth,
    infoareaWidth,
    minContentWidth: MIN_CONTENT_WIDTH,
    hasInfoArea,
  })

  // The expanded list covers the participant view, so return it to default width once one is picked.
  useEffect(() => {
    if (selectedUid) setSidebarExpanded(false)
  }, [selectedUid])

  // whenever we go from no-infoarea to having an infoarea, we want to show it
  useEffect(() => {
    setInfoAreaOpen(hasInfoArea)
  }, [hasInfoArea])

  return (
    <SidebarControlsContext.Provider value={sidebarControls}>
      <Box height="100%" display="flex">
        {sidebar && (
          <FoldableDrawer
            open={!isCollapsed}
            onTogglerClick={toggleCollapsed}
            hideToggler={Boolean(collapsedSidebarControl)}
            alignment="left"
            dockedWidth={sidebarWidth}
            paperWidth={sidebarPaperWidth}
            paperBackgroundColor={sidebarPaperBackgroundColor}
          >
            {sidebar}
          </FoldableDrawer>
        )}
        <Box
          flexGrow={1}
          minWidth={0}
          position="relative"
          className={cx({
            [sidebarContentStyles.content]: hasSidebar,
            [infoareaContentStyles.content]: hasInfoArea,
            [sidebarContentStyles.contentSwift]: !sidebarCollapsed,
            [infoareaContentStyles.contentSwift]: infoareaOpen,
          })}
        >
          {children}
          {isCollapsed && collapsedSidebarControl && (
            <Box className={styles.collapsedSidebarControl}>
              {collapsedSidebarControl}
              <Divider orientation="vertical" className={styles.collapsedSidebarDivider} />
            </Box>
          )}
        </Box>
        {infoarea && (
          <FoldableDrawer
            open={infoareaOpen}
            onTogglerClick={toggleInfoarea}
            alignment="right"
            dockedWidth={infoareaWidth}
            paperBackgroundColor={infoareaPaperBackgroundColor}
          >
            {infoarea}
          </FoldableDrawer>
        )}
      </Box>
    </SidebarControlsContext.Provider>
  )
}

export default Container
